import { useState, useEffect, RefObject } from 'react';

export const useScrollState = (ref?: RefObject<HTMLElement>) => {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = (event: Event) => {
      const target = event.target;
      if (!target) return;

      if (ref && ref.current) {
        if (target === document) {
          setIsScrolled(window.scrollY > 0);
        } else {
          const el = target as HTMLElement;
          if (typeof el.contains === 'function' && el.contains(ref.current)) {
            setIsScrolled(el.scrollTop > 0);
          }
        }
      } else {
        setIsScrolled(window.scrollY > 0);
      }
    };

    window.addEventListener('scroll', handleScroll, { capture: true, passive: true });

    if (ref && ref.current) {
      let parent = ref.current.parentElement;
      while (parent) {
        if (parent.scrollTop > 0) {
          setIsScrolled(true);
          break;
        }
        parent = parent.parentElement;
      }
    } else {
      setIsScrolled(window.scrollY > 0);
    }

    return () => {
      window.removeEventListener('scroll', handleScroll, { capture: true });
    };
  }, [ref]);

  return isScrolled;
};

export const useScrollDirection = (threshold: number = 16) => {
  const [scrollDirection, setScrollDirection] = useState<'up' | 'down'>('up');

  useEffect(() => {
    let lastScrollY = window.scrollY;

    const updateScrollDirection = (event: Event) => {
      const target = event.target;
      let scrollY = window.scrollY;

      if (target && target !== document) {
        const el = target as HTMLElement;
        if (typeof el.scrollTop === 'number') {
          scrollY = el.scrollTop;
        }
      }

      if (scrollY <= threshold) {
        setScrollDirection('up');
        lastScrollY = scrollY;
        return;
      }

      const diff = scrollY - lastScrollY;
      if (Math.abs(diff) >= threshold) {
        setScrollDirection(diff > 0 ? 'down' : 'up');
        lastScrollY = scrollY;
      }
    };

    window.addEventListener('scroll', updateScrollDirection, { capture: true, passive: true });

    return () => {
      window.removeEventListener('scroll', updateScrollDirection, { capture: true });
    };
  }, [threshold]);

  return scrollDirection;
};
